import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useProjectContent } from "../../hooks/useProjectContent";
import styles from "../../style/project/projectReadingProgress.module.css";

const CASE_STUDY_PATH = /^\/projects\/[^/]+$/;

const getScrollProgress = (content) => {
  if (!content.isConnected) {
    return 0;
  }

  const page = content.closest(".project-page") ?? content;
  const rect = page.getBoundingClientRect();
  const pageTop = window.scrollY + rect.top;
  const pageHeight = page.offsetHeight;
  const viewportHeight = window.innerHeight;
  const scrollableDistance = pageHeight - viewportHeight;

  if (scrollableDistance <= 0) {
    const pastTop = window.scrollY >= pageTop;
    return pastTop ? 100 : 0;
  }

  const scrolled = window.scrollY - pageTop;
  return Math.min(100, Math.max(0, (scrolled / scrollableDistance) * 100));
};

const ProjectScrollProgress = () => {
  const { pathname } = useLocation();
  const isCaseStudy = CASE_STUDY_PATH.test(pathname);
  const content = useProjectContent(isCaseStudy, pathname);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!content) {
      setProgress(0);
      return undefined;
    }

    let rafId = 0;

    const update = () => {
      if (!content.isConnected) {
        setProgress(0);
        return;
      }
      setProgress(getScrollProgress(content));
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    const resizeObserver = new ResizeObserver(onScrollOrResize);
    resizeObserver.observe(content);
    const page = content.closest(".project-page");
    if (page && page !== content) {
      resizeObserver.observe(page);
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      resizeObserver.disconnect();
    };
  }, [content]);

  if (!isCaseStudy || !content) return null;

  const roundedProgress = Math.round(progress);

  return (
    <div
      className={styles.projectScrollProgress}
      role="progressbar"
      aria-label="Case study reading progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={roundedProgress}
    >
      <div
        className={styles.projectScrollProgressBar}
        style={{ transform: `scaleX(${progress / 100})` }}
      />
    </div>
  );
};

export default ProjectScrollProgress;
